import React from "react";
import { Button, Result, Typography } from "antd";

const { Paragraph, Text } = Typography;

/**
 * Catches render/lifecycle errors below it and shows a recoverable fallback.
 *
 * Without a boundary React 18 unmounts the whole root when any component
 * throws while rendering, which empties #root and leaves a completely white,
 * unresponsive page with nothing in the UI to explain it.
 *
 * Props:
 *   resetKey  - when this value changes the boundary clears its error, so
 *               navigating to another route recovers without a full reload.
 *   scope     - "page" keeps the surrounding shell (sidebar/header) usable;
 *               "app" is the outermost last-resort boundary.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, componentStack: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep the stack reachable in production, where the fallback replaces the
    // blank screen and there would otherwise be no trace of what threw.
    console.error("[ErrorBoundary] unhandled render error:", error, info?.componentStack);
    this.setState({ componentStack: info?.componentStack || null });
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null, componentStack: null });
    }
  }

  handleRetry = () => {
    this.setState({ error: null, componentStack: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { error, componentStack } = this.state;

    if (!error) {
      return this.props.children;
    }

    const isAppScope = this.props.scope === "app";

    return (
      <div style={ { padding: 24 } }>
        <Result
          status="warning"
          title="This screen could not be displayed"
          subTitle="Something went wrong while rendering this page. The rest of the application is still usable."
          extra={ [
            <Button key="retry" type="primary" onClick={ this.handleRetry }>
              Try again
            </Button>,
            <Button key="reload" onClick={ this.handleReload }>
              Reload page
            </Button>,
          ] }
        >
          <Paragraph>
            <Text type="danger" copyable={ { text: `${error.message}\n${componentStack || ""}` } }>
              { error.message || String(error) }
            </Text>
          </Paragraph>
          { componentStack ? (
            <details style={ { marginTop: 8 } }>
              <summary style={ { cursor: "pointer", color: "#687980" } }>Technical details</summary>
              <pre
                style={ {
                  marginTop: 8,
                  maxHeight: 240,
                  overflow: "auto",
                  fontSize: 11,
                  lineHeight: 1.5,
                  color: "#687980",
                  whiteSpace: "pre-wrap",
                } }
              >
                { componentStack }
              </pre>
            </details>
          ) : null }
          { isAppScope ? null : (
            <Paragraph type="secondary" style={ { marginTop: 12, marginBottom: 0 } }>
              You can continue by choosing another screen from the menu.
            </Paragraph>
          ) }
        </Result>
      </div>
    );
  }
}

export default ErrorBoundary;
